/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import { Box, Container, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import CustomRightImage from "@components/CustomRightImagev2";
import { InactivityHandler } from "@components/InactivityHandler";
import { RootState, useAppDispatch } from "@store/store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError, DocumentContract } from "@interfaces/response";
import * as Sentry from "@sentry/react";
import CenteredLoader from "@components/CenteredLoader";
import MifielScript from "@components/Firma/MifielScript";
import FirmaMiFiel from "@components/Firma/FirmaMiFiel";
import { useLazyGetContractualDocumentLoanApplicationQuery } from "@api/documents";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import useInterval from "@hooks/useInterval";
import { STATUS_CATALOG } from "@helpers/constantes";
import { setLoanApplication, setLoanStatus } from "@store/slices/registerSlice";

const SECONDS_TO_FETCH = 120;

const FirmaDocumentosPF = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);

  const [getDocumentByApplicationClient] = useLazyGetContractualDocumentLoanApplicationQuery();
  const [getLoanApplicationById] = useLazyGetLoanApplicationByIdQuery();

  const [isLoading, setIsLoading] = useState(true);
  const [documentsToSign, setDocumentsToSign] = useState<DocumentContract[]>([]);

  const loanInterval = useInterval(async () => {
    try {
      const { firma_representante_legal } = STATUS_CATALOG;
      const loan_application = await getLoanApplicationById(application.id).unwrap();
      if (loan_application?.status_catalog && loan_application.status_catalog.code !== firma_representante_legal.code) {
        dispatch(setLoanApplication(loan_application));
        dispatch(setLoanStatus(loan_application.status_catalog.code));
        navigate(loan_application.status_catalog.url_actual);
      }
    } catch (error) {
      console.error("Ha ocurrido un error al obtener la aplicación");
    }
  }, SECONDS_TO_FETCH);

  useEffect(() => {
    loanInterval.setIsActiveInterval(true);

    return () => {
      loanInterval.setIsActiveInterval(false);
    };
  }, []);

  const fetchInitialData = useCallback(async () => {
    try {
      if (!application.id || !clientDetails.id) {
        throw new Error("Application ID or client ID is required");
      }

      setIsLoading(true);

      const documents = await getDocumentByApplicationClient({
        loanApplicationId: application.id,
        clientId: clientDetails.id,
      }).unwrap();

      setDocumentsToSign(documents);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "FirmaDocumentosPF-fetchInitialData",
          values: {
            applicationId: application.id,
            clientId: clientDetails.id,
          },
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [application.id, clientDetails.id, dispatch, getDocumentByApplicationClient]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <MifielScript />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {isLoading ? (
            <CenteredLoader />
          ) : (
            <>
              <Typography variant={"tofu_title"} textAlign="center" color={"primary.main"}>
                Firma de documentos
              </Typography>

              <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 3 : 4, textAlign: "center" }}>
                <Typography
                  sx={{ color: "primary.dark", mb: 1 }}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  textAlign="center"
                  fontWeight={500}
                >
                  En caso de que tengas alguna duda no dudes en contactar a tu asesor
                </Typography>

                <Box mt={isMobileOrTablet ? 2 : 4}>
                  <FirmaMiFiel documents={documentsToSign} />
                </Box>
              </Container>
            </>
          )}
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default FirmaDocumentosPF;
